import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Milestone, Rule, Ruleset } from 'types/dt-graphql-apollo-v2';
import { Title, Body, Button, Flex, Modal, Chip } from '@cognite/cogs.js';
import { Spinner, VerticalDivider } from 'components';
import { notification } from 'antd';
import pluralize from 'pluralize';
import { CopyRulesOptions, useCopyRules } from './useCopyRules';

import {
  checkIfCollectionIsSelected,
  checkIfRuleIsSelected,
  checkIfRulesetIsSelected,
  independentRulesId,
  independentRulesName,
  filterSelectedRulesets,
  StepName,
  CopyRulesPlaceholderType,
  addMilestoneToCopyPlaceholder,
  addRulesToCopyPlaceholder,
  addRulesetsToCopyPlaceholder,
  getCountsFromCopyPlaceholder,
  checkIfItemIsDisabled,
  getAllMilestonesRules,
  getMilestonesWithRulesetRules,
  getAllMilestonesRulesets,
} from './helpers';
import { CopyItem } from './components/CopyItem';
import { SelectedRulesInformation, StepHeader } from './components/StepHeader';

type CopyRulesModalProps = {
  visible: boolean;
  destinationMilestone?: Milestone;
  destinationRuleset?: Ruleset | null;
  milestones: Milestone[];
  onCancel: VoidFunction;
  onCopyRulesSuccess: VoidFunction;
};

export const CopyRulesModal = ({
  visible,
  destinationMilestone,
  destinationRuleset = null,
  milestones: originalMilestones,
  onCancel,
  onCopyRulesSuccess,
}: CopyRulesModalProps) => {
  const [currentStep, setCurrentStep] = useState<StepName>(StepName.COLLECTION);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState<Milestone | null>(
    null
  );
  const [currentRuleset, setCurrentRuleset] = useState<Ruleset | null>(null);
  const [copyRulesPlaceholder, setCopyRulesPlaceholder] =
    useState<CopyRulesPlaceholderType>({});

  const { copyRules } = useCopyRules();

  const milestones = getMilestonesWithRulesetRules(originalMilestones);
  const allRules = getAllMilestonesRules(milestones);
  const allRulesets = getAllMilestonesRulesets(milestones);
  const independentRules =
    currentMilestone?.rules?.items.filter((r) => !r?.rulesets?.items.length) ||
    [];
  const rulesetWithIndependentRules = {
    externalId: independentRulesId,
    name: independentRulesName,
    rules: { items: independentRules },
    space: '',
  } as Ruleset;

  const canCopyRules =
    Object.values(copyRulesPlaceholder).length > 0 && !isLoading;

  /** Either add the selected milestone and its rulesets + rules into the placeholder, or remove it if it's already added. */
  const handleMilestonePress = (milestone: Milestone) => {
    const milestoneAlreadyAdded = checkIfCollectionIsSelected(
      milestone.externalId,
      copyRulesPlaceholder
    );

    if (milestoneAlreadyAdded) {
      const {
        [milestone.externalId]: _removedMilestoneId,
        ...newCopyRulesPlaceholder
      } = copyRulesPlaceholder;
      setCopyRulesPlaceholder(newCopyRulesPlaceholder);
    } else {
      const updatedPlaceholder = addMilestoneToCopyPlaceholder(
        milestone,
        copyRulesPlaceholder,
        destinationRuleset
      );
      setCopyRulesPlaceholder(updatedPlaceholder);
    }
  };

  /** Either add the selected ruleset and its rules into the placeholder, or remove it if it's already added. */
  const handleRulesetPress = (ruleset: Ruleset) => {
    const milestoneId = currentMilestone?.externalId;

    if (!milestoneId) return;

    const rulesetAlreadyAdded = checkIfRulesetIsSelected(
      copyRulesPlaceholder,
      ruleset.externalId,
      milestoneId
    );

    if (rulesetAlreadyAdded) {
      const { [ruleset.externalId]: _removedRulesetId, ...newRulesetState } =
        copyRulesPlaceholder[milestoneId];

      // Check if all the rulesets have been removed from the selected milestone
      const milestoneIsEmpty = !Object.keys(newRulesetState).length;

      if (milestoneIsEmpty) {
        // If a milestone is empty, then remove it from the selected options
        const {
          [milestoneId]: _removedMilestoneId,
          ...newCopyRulesPlaceholder
        } = copyRulesPlaceholder;
        setCopyRulesPlaceholder(newCopyRulesPlaceholder);
      } else {
        setCopyRulesPlaceholder((state) => ({
          ...state,
          [milestoneId]: newRulesetState,
        }));
      }
    } else {
      const updatedPlaceholder = addRulesetsToCopyPlaceholder(
        [ruleset],
        milestoneId,
        copyRulesPlaceholder,
        destinationRuleset
      );
      setCopyRulesPlaceholder(updatedPlaceholder);
    }
  };

  /** Either add the selected rule into the placeholder, or remove it if it's already added. */
  const handleRulePress = (rule: Rule) => {
    const milestoneId = currentMilestone?.externalId;
    const rulesetId = currentRuleset?.externalId;

    if (!milestoneId || !rulesetId) return;

    const ruleAlreadyAdded = checkIfRuleIsSelected(
      copyRulesPlaceholder,
      milestoneId,
      rulesetId,
      rule.externalId
    );

    if (ruleAlreadyAdded) {
      const updatedRulesList = copyRulesPlaceholder[milestoneId][
        rulesetId
      ].filter((ruleId) => ruleId !== rule.externalId);
      copyRulesPlaceholder[milestoneId][rulesetId] = updatedRulesList;

      setCopyRulesPlaceholder({ ...copyRulesPlaceholder });
    } else {
      const updatedPlaceholder = addRulesToCopyPlaceholder(
        [rule],
        milestoneId,
        rulesetId,
        copyRulesPlaceholder,
        destinationRuleset
      );
      setCopyRulesPlaceholder(updatedPlaceholder);
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      setCurrentStep(StepName.COLLECTION);
      onCancel();
    }
  };

  const handleAddRules = async () => {
    setIsLoading(true);
    const { rulesetsCount, rulesCount } =
      getCountsFromCopyPlaceholder(copyRulesPlaceholder);

    const successMessage = destinationRuleset
      ? `Copied ${pluralize('rule', rulesCount, true)} into the rulset "${
          destinationRuleset?.name
        }".`
      : `Copied ${pluralize('ruleset', rulesetsCount, true)} and ${pluralize(
          'rule',
          rulesCount,
          true
        )} into the collection "${destinationMilestone?.name}".`;

    const options: CopyRulesOptions = {
      placeholderData: copyRulesPlaceholder,
      allRulesets,
      allRules,
      destinationMilestoneId: destinationMilestone?.externalId,
      destinationRulesetId: destinationRuleset?.externalId,
      onSuccess: () => {
        notification.success({
          message: 'Successfully copied the selected rules!',
          description: successMessage,
        });
      },
      onError: (err) => {
        notification.error({
          message: 'Could not copy the selected rules.',
          description: err.toString(),
        });
      },
    };

    await copyRules(options);
    onCopyRulesSuccess();
    handleClearPlaceholder();
    setIsLoading(false);
  };

  const handleClearPlaceholder = () => {
    setCopyRulesPlaceholder({});
    setCurrentStep(StepName.COLLECTION);
  };

  const goToRulesSelection = (ruleset: Ruleset | null) => {
    setCurrentStep(currentStep + 1);
    setCurrentRuleset(ruleset);
  };

  /**  Select or diselect all the rules from the currently opened rulset. */
  const selectAllRules = () => {
    if (!currentRuleset?.externalId || !currentMilestone?.externalId) return;

    const filteredRules =
      currentRuleset?.rules?.items.filter(
        (rule) =>
          rule &&
          !checkIfRuleIsSelected(
            copyRulesPlaceholder,
            currentMilestone?.externalId,
            currentRuleset?.externalId,
            rule.externalId
          )
      ) || [];

    // Update state only if there is a change
    if (filteredRules.length > 0) {
      const updatedPlaceholder = addRulesToCopyPlaceholder(
        filteredRules,
        currentMilestone?.externalId,
        currentRuleset?.externalId,
        copyRulesPlaceholder
      );
      setCopyRulesPlaceholder(updatedPlaceholder);
    }
  };

  /**  Select all the rulesets from the currently opened milestone. */
  const selectAllRulesets = () => {
    if (!currentMilestone?.externalId) return;

    // Get all the rulesets linked to the current milestone
    const rulesetsToIterate = [
      ...(currentMilestone?.rulesets?.items || []),
      rulesetWithIndependentRules,
    ];

    // Filter out the already selected rulesets
    const filteredRulesets = filterSelectedRulesets(
      rulesetsToIterate,
      currentMilestone.externalId,
      copyRulesPlaceholder
    );

    // Update state only if there is a change
    if (filteredRulesets.length > 0) {
      const updatedPlaceholder = addRulesetsToCopyPlaceholder(
        filteredRulesets,
        currentMilestone.externalId,
        copyRulesPlaceholder,
        destinationRuleset
      );
      setCopyRulesPlaceholder(updatedPlaceholder);
    }
  };

  const renderRulesetsStep = () => {
    const rulesets = [
      rulesetWithIndependentRules,
      ...(currentMilestone?.rulesets?.items || []),
    ];

    return (
      <>
        {rulesets.map(
          (ruleset) =>
            ruleset && (
              <CopyItem
                key={ruleset.externalId}
                title={ruleset.name}
                type={StepName.RULESET}
                item={ruleset}
                hasArrow
                isSelected={checkIfRulesetIsSelected(
                  copyRulesPlaceholder,
                  ruleset.externalId,
                  currentMilestone?.externalId
                )}
                onItemClick={() => handleRulesetPress(ruleset)}
                onArrowClick={() => goToRulesSelection(ruleset)}
                destinationRuleset={destinationRuleset}
              />
            )
        )}
      </>
    );
  };

  const renderRulesStep = () => (
    <>
      {currentRuleset?.rules?.items.map(
        (rule) =>
          rule && (
            <CopyItem
              key={rule.externalId}
              title={rule.name!}
              type={StepName.RULE}
              item={rule}
              isSelected={checkIfRuleIsSelected(
                copyRulesPlaceholder,
                currentMilestone?.externalId,
                currentRuleset?.externalId,
                rule.externalId
              )}
              onItemClick={() => handleRulePress(rule)}
              destinationRuleset={destinationRuleset}
            />
          )
      )}
    </>
  );

  const renderContent = () => {
    switch (currentStep) {
      case StepName.RULE:
        return (
          <>
            <StepHeader
              backButtonLabel="Go back to the Rulesets selecting step."
              onBackButtonClick={() => {
                setCurrentStep(StepName.RULESET);
                setCurrentRuleset(null);
              }}
              backButtonContent={
                <>
                  {currentMilestone?.name}
                  <VerticalDivider />
                  {currentRuleset?.name}
                </>
              }
              content={
                <SelectedRulesInformation
                  copyRulesPlaceholder={copyRulesPlaceholder}
                />
              }
              selectAllButtonLabel="Select all the available rules."
              onSelectAllButtonClick={() => selectAllRules()}
              selectAllDisabled={checkIfItemIsDisabled(
                currentRuleset as Ruleset,
                StepName.RULESET,
                destinationRuleset
              )}
            />
            <StyledList direction="column" gap={10}>
              {renderRulesStep()}
            </StyledList>
          </>
        );

      case StepName.RULESET:
        return (
          <>
            <StepHeader
              backButtonLabel="Go back to the Collection selecting step."
              onBackButtonClick={() => {
                setCurrentMilestone(null);
                setCurrentStep(StepName.COLLECTION);
              }}
              backButtonContent={<>{currentMilestone?.name}</>}
              content={
                <SelectedRulesInformation
                  copyRulesPlaceholder={copyRulesPlaceholder}
                />
              }
              selectAllButtonLabel="Select all the available rulesets and their rules."
              onSelectAllButtonClick={() => selectAllRulesets()}
              selectAllDisabled={checkIfItemIsDisabled(
                currentMilestone as Milestone,
                StepName.COLLECTION,
                destinationRuleset
              )}
            />
            <StyledList direction="column" gap={10}>
              {renderRulesetsStep()}
            </StyledList>
          </>
        );

      default:
        return (
          <>
            <StyledHeader gap={10} alignItems="center">
              <Title level={5}>Select rule(s) from a collection</Title>
              <SelectedRulesInformation
                copyRulesPlaceholder={copyRulesPlaceholder}
              />
            </StyledHeader>
            <StyledList direction="column" gap={10}>
              {milestones.map((milestone) => (
                <CopyItem
                  key={milestone.externalId}
                  title={milestone.name}
                  type={StepName.COLLECTION}
                  item={milestone}
                  hasArrow
                  isSelected={checkIfCollectionIsSelected(
                    milestone.externalId,
                    copyRulesPlaceholder
                  )}
                  onItemClick={() => handleMilestonePress(milestone)}
                  onArrowClick={() => {
                    setCurrentStep(currentStep + 1);
                    setCurrentMilestone(milestone);
                  }}
                  destinationRuleset={destinationRuleset}
                />
              ))}
            </StyledList>
          </>
        );
    }
  };

  return (
    <StyledModal visible={visible} onCancel={handleCancel} hideFooter={true}>
      <StyledFlex gap={4} direction="column">
        <Flex gap={8} direction="row">
          <Title level={3}>Add existing rules</Title>
          <Chip
            type="success"
            label={destinationMilestone?.name || 'Default collection'}
            hideTooltip
          />
          {destinationRuleset && (
            <Chip type="neutral" label={destinationRuleset?.name} hideTooltip />
          )}
        </Flex>
        <Body level={2}>Add a copy of one or more rules and rulesets</Body>
      </StyledFlex>
      {renderContent()}
      <Flex justifyContent="flex-end" gap={8}>
        <Button disabled={isLoading} onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          disabled={!canCopyRules}
          loading={isLoading}
          onClick={handleAddRules}
          type="primary"
        >
          Add rules
        </Button>
      </Flex>
      {isLoading && (
        <StyledSpinner>
          <Spinner />
        </StyledSpinner>
      )}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  min-width: 50vw;
  min-height: 80vh;
  .cogs-modal-close {
    padding: 1rem;
    cursor: pointer;
  }
  .cogs-modal-content {
    width: 100%;
    min-height: 65vh;
  }
`;

const StyledHeader = styled(Flex)`
  margin: 0.5rem 0;
  .cogs-title-5 {
    padding: 0.5rem 0;
  }
`;

const StyledList = styled(Flex)`
  padding: 0.2rem;
  overflow: auto;
  height: 60vh;
`;

const StyledFlex = styled(Flex)`
  max-width: 90%;
`;

const StyledSpinner = styled.div`
  position: absolute;
  top: 0;
  min-width: calc(50vw - 2rem);
  height: 100%;
`;
